/**
 * Fires a GA4 page_view event on route change for a fixed allowlist of
 * paths. Mounted once in App.tsx inside the <Router> so it can call
 * useLocation(). The initial load fires one pageview too — the analytics
 * module suppresses gtag's own initial page_view (send_page_view: false)
 * so we don't double-count.
 *
 * Allowlist rather than "track everything" is deliberate:
 *   - /admin, /api-keys, /support etc. are internal admin surfaces
 *     where visits aren't interesting product signal.
 *   - It also keeps /alerts/:id-style deep links from spamming distinct
 *     paths — we only care that someone hit "the alerts page."
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../analytics';

// Exact paths (or match-fns) we care about. Keep the labels stable so
// GA4 doesn't fragment reports if we ever rename a route.
const TRACKED: { match: (path: string) => boolean; label: string }[] = [
  { match: (p) => p === '/',                    label: 'Home' },
  { match: (p) => p === '/map',                 label: 'Map' },
  { match: (p) => p === '/research',            label: 'Research' },
  { match: (p) => p === '/alerts',              label: 'Alerts' },
  { match: (p) => p === '/explore/venezuela',   label: 'Explore — Venezuela' },
];

export function usePageTracking(): void {
  const location = useLocation();
  useEffect(() => {
    const hit = TRACKED.find((t) => t.match(location.pathname));
    if (!hit) return;
    trackPageView(location.pathname, `Mosaics — ${hit.label}`);
  }, [location.pathname]);
}
